<template>
  <header
    class="bg-white/80 dark:bg-dark-bg/80 border-b border-slate-200 dark:border-white/5 px-8 py-4 flex justify-between items-center sticky top-0 z-40 backdrop-blur-xl transition-colors duration-300"
  >
    <div class="flex items-center gap-4">
      <h1
        class="text-lg font-black text-slate-800 dark:text-white tracking-widest uppercase italic"
      >
        {{ currentRouteName }}
      </h1>
    </div>

    <div class="flex items-center gap-6 relative">
      <!-- Language Switcher -->
      <div
        class="flex items-center gap-1 bg-slate-100 dark:bg-dark-input p-1 rounded-xl border border-slate-200 dark:border-white/5"
      >
        <button
          @click="changeLocale('en')"
          :class="
            locale === 'en'
              ? 'bg-white dark:bg-dark-surface shadow-sm text-primary'
              : 'text-slate-500 dark:text-slate-400 hover:text-blue-600 dark:hover:text-primary'
          "
          class="px-2.5 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all"
        >
          EN
        </button>
        <button
          @click="changeLocale('ar')"
          :class="
            locale === 'ar'
              ? 'bg-white dark:bg-dark-surface shadow-sm text-primary'
              : 'text-slate-500 dark:text-slate-400 hover:text-blue-600 dark:hover:text-primary'
          "
          class="px-2.5 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all"
        >
          AR
        </button>
      </div>

      <!-- Theme Toggle -->
      <button
        @click="toggleTheme"
        class="p-2.5 rounded-xl bg-slate-100 dark:bg-dark-input text-slate-500 dark:text-slate-400 hover:text-blue-600 dark:hover:text-primary transition-all border border-slate-200 dark:border-white/5"
        :title="$t('toggle_theme')"
      >
        <i v-if="isDark" class="fas fa-sun"></i>
        <i v-else class="fas fa-moon"></i>
      </button>

      <!-- Search Icon -->
      <button
        class="text-slate-400 dark:text-slate-500 hover:text-blue-600 dark:hover:text-white transition-colors"
      >
        <i class="fas fa-search h-5 w-5"></i>
      </button>

      <!-- Avatar + Username -->
      <button
        @click="toggleDropdown"
        class="flex items-center gap-3 p-1.5 pr-4 rounded-2xl bg-slate-100 dark:bg-dark-input border border-slate-200 dark:border-white/5 hover:border-blue-600/20 dark:hover:border-white/10 transition-all focus:outline-none group shadow-sm dark:shadow-lg dark:shadow-black/20"
      >
        <div
          class="w-9 h-9 rounded-xl bg-gradient-to-br from-primary to-blue-600 flex items-center justify-center text-white font-black shadow-inner"
        >
          {{ username.charAt(0).toUpperCase() }}
        </div>
        <div class="text-left hidden md:block">
          <p
            class="text-xs font-black text-slate-800 dark:text-white leading-none mb-1"
          >
            {{ username }}
          </p>
          <p
            class="text-[10px] text-slate-500 font-bold uppercase tracking-tighter"
          >
            {{ email.split("@")[0] }}
          </p>
        </div>
        <i
          class="fas fa-chevron-down w-3.5 h-3.5 text-slate-400 dark:text-slate-500 transition-transform duration-300 group-hover:text-blue-600 dark:group-hover:text-white"
          :class="{ 'rotate-180': open }"
        ></i>
      </button>

      <!-- User Dropdown Menu -->
      <transition name="dropdown">
        <div
          v-if="open"
          class="absolute right-0 top-16 w-56 bg-white dark:bg-dark-surface rounded-2xl shadow-2xl border border-slate-200 dark:border-white/5 z-50 overflow-hidden backdrop-blur-2xl"
        >
          <div
            class="px-5 py-4 border-b border-slate-100 dark:border-white/5 bg-slate-50 dark:bg-white/5"
          >
            <p class="text-sm font-black text-slate-800 dark:text-white">
              {{ username }}
            </p>
            <p
              class="text-[10px] text-slate-500 font-bold truncate mt-0.5 uppercase tracking-widest"
            >
              {{ email }}
            </p>
          </div>

          <div class="p-2">
            <button
              class="w-full flex items-center gap-3 px-4 py-2.5 text-xs font-bold text-slate-500 dark:text-slate-400 hover:text-blue-600 dark:hover:text-white hover:bg-slate-50 dark:hover:bg-white/5 rounded-xl transition-all"
            >
              <i class="fas fa-user-cog"></i> {{ $t("profile_settings") }}
            </button>
            <button
              @click="logoutUser"
              class="w-full flex items-center gap-3 px-4 py-2.5 text-xs font-bold text-red-500 dark:text-red-400 hover:text-white hover:bg-red-500 rounded-xl transition-all mt-1"
            >
              <i class="fas fa-sign-out-alt"></i> {{ $t("sign_out") }}
            </button>
          </div>
        </div>
      </transition>

      <!-- Notification Bell -->
      <div class="relative">
        <button
          @click="toggleNotificationDropdown"
          class="relative p-2.5 rounded-xl bg-slate-100 dark:bg-dark-input border border-slate-200 dark:border-white/5 hover:bg-slate-200 dark:hover:bg-dark-hover transition-all"
        >
          <i class="fas fa-bell text-slate-500 dark:text-slate-400"></i>
          <span
            v-if="store.unreadCount"
            class="absolute -top-1 -right-1 bg-red-500 text-white text-[9px] font-bold rounded-full w-4 h-4 flex items-center justify-center shadow-lg border-2 border-white dark:border-dark-bg"
          >
            {{ store.unreadCount }}
          </span>
        </button>

        <!-- Notification Dropdown -->
        <div
          v-if="notificationDropdown"
          class="absolute right-0 mt-2 w-64 bg-white dark:bg-dark-surface rounded-2xl shadow-2xl border border-slate-200 dark:border-white/5 z-50 overflow-hidden backdrop-blur-2xl"
        >
          <div
            v-for="n in store.notifications"
            :key="n.id"
            class="p-2 border-b hover:bg-gray-100 dark:hover:bg-white/5 cursor-pointer"
            @click="markRead(n.id)"
          >
            <div class="font-bold text-sm">{{ n.title }}</div>
            <div class="text-xs text-gray-500 dark:text-gray-300">
              {{ n.body }}
            </div>
            <div class="text-[10px] text-gray-400 dark:text-gray-500">
              {{ new Date(n.created_at).toLocaleString() }}
            </div>
          </div>
          <div
            v-if="store.notifications.length === 0"
            class="p-2 text-gray-500 dark:text-gray-400"
          >
            {{ $t("no_notifications") }}
          </div>
        </div>
      </div>
    </div>
  </header>
</template>

<script>
import authService from "@/services/auth.service";
import { isDark, toggleTheme } from "@/utils/theme";
import { useI18n } from "vue-i18n";
import { ref, onMounted } from "vue";
import { useNotificationStore } from "@/stores/notification";

export default {
  name: "Navbar",
  data() {
    return {
      username: "User",
      email: "",
      open: false,
    };
  },
  setup() {
    const { locale } = useI18n();
    const changeLocale = (lang) => {
      locale.value = lang;
    };

    const store = useNotificationStore();
    const notificationDropdown = ref(false);

    const toggleNotificationDropdown = () => {
      notificationDropdown.value = !notificationDropdown.value;
    };

    const markRead = (id) => {
      store.markAsRead(id);
    };

    onMounted(() => {
      const user = authService.getUser();
      if (user) {
        store.init(user.id); // Init Echo subscriptions
      }
    });

    return {
      isDark,
      toggleTheme,
      locale,
      changeLocale,
      store,
      notificationDropdown,
      toggleNotificationDropdown,
      markRead,
    };
  },
  computed: {
    currentRouteName() {
      return this.$route.name || "Overview";
    },
  },
  methods: {
    toggleDropdown(e) {
      e.stopPropagation();
      this.open = !this.open;
    },

    handleClickOutside(e) {
      if (this.open && !this.$el.contains(e.target)) {
        this.open = false;
      }
    },

    logoutUser() {
      this.open = false;
      authService.logout();
      this.$router.push("/login");
    },
  },

  mounted() {
    const user = authService.getUser();
    if (user) {
      this.username = user.username || user.name || user.email || "User";
      this.email = user.email || "";
    }
    document.addEventListener("click", this.handleClickOutside);
  },
  unmounted() {
    document.removeEventListener("click", this.handleClickOutside);
  },
};
</script>

<style scoped>
.dropdown-enter-active,
.dropdown-leave-active {
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}
.dropdown-enter-from {
  opacity: 0;
  transform: translateY(-10px) scale(0.95);
}
.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-10px) scale(0.95);
}
</style>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(-5px);
}
</style>
